import { useLazyQuery } from '@apollo/client/react'
import { addYears } from 'date-fns'
import { useCallback, useMemo, useRef, useState } from 'react'
import { toast, Heading, Text, Link } from '@buffer-mono/popcorn'

import { graphql } from '../../../../gql'

const GetAnnualUpgradePreviewInvoice = graphql(/* GraphQL */ `
  query GetAnnualUpgradePreviewInvoice($organizationId: OrganizationId!) {
    getAnnualUpgradePreviewInvoice(input: { organizationId: $organizationId }) {
      ... on GetPreviewInvoiceResponse {
        chargeAmount
        creditAmount
        isScheduledChange
        nextBillingDate
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

export interface AnnualUpgradePreviewInvoiceData {
  /** Formatted date of the next annual renewal (one year after the preview period start). */
  nextBillingDate: string
  dueNow: number
}

const formatDateString = (dateString: string | null | undefined): string => {
  if (!dateString) return ''

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(String(dateString)))
}

/**
 * Invoice preview `nextBillingDate` is the start of the new annual period.
 * The renewal after a year of annual billing is exactly one calendar year later.
 */
const formatRenewsOnDate = (
  periodStartFromPreview: string | null | undefined,
): string => {
  if (periodStartFromPreview == null) return ''

  const periodStart = new Date(String(periodStartFromPreview))
  if (Number.isNaN(periodStart.getTime())) return ''

  return formatDateString(addYears(periodStart, 1).toISOString())
}

interface UseGetAnnualUpgradePreviewInvoiceOptions {
  showErrorToast?: boolean
}

interface UseGetAnnualUpgradePreviewInvoiceReturn {
  fetchAnnualUpgradePreview: (
    organizationId: string,
  ) => Promise<void> | undefined
  loading: boolean
  error: Error | undefined
  data: AnnualUpgradePreviewInvoiceData | undefined
  clearData: () => void
}

export function useGetAnnualUpgradePreviewInvoice(
  options: UseGetAnnualUpgradePreviewInvoiceOptions = {},
): UseGetAnnualUpgradePreviewInvoiceReturn {
  const { showErrorToast = true } = options
  const [fetchPreviewInvoice, { loading, error }] = useLazyQuery(
    GetAnnualUpgradePreviewInvoice,
  )

  const [invoiceData, setInvoiceData] = useState<
    AnnualUpgradePreviewInvoiceData | undefined
  >(undefined)

  const requestIdRef = useRef(0)

  const fetchAnnualUpgradePreview = useCallback(
    async (organizationId: string): Promise<void> => {
      const currentId = ++requestIdRef.current

      try {
        const result = await fetchPreviewInvoice({
          variables: { organizationId },
        })

        if (result.error) {
          throw result.error
        }

        const response = result.data?.getAnnualUpgradePreviewInvoice
        if (currentId !== requestIdRef.current) return

        if (response?.__typename === 'VoidMutationError') {
          setInvoiceData(undefined)
          return
        }

        if (response?.__typename === 'GetPreviewInvoiceResponse') {
          const chargeAmount = Number(response.chargeAmount ?? 0)

          if (currentId !== requestIdRef.current) return
          setInvoiceData({
            dueNow: chargeAmount,
            nextBillingDate: formatRenewsOnDate(
              response.nextBillingDate == null
                ? undefined
                : String(response.nextBillingDate),
            ),
          })
        } else {
          setInvoiceData(undefined)
        }
      } catch {
        if (currentId === requestIdRef.current) {
          if (showErrorToast) {
            toast.error(
              <>
                <Heading size="xsmall" style={{ marginBottom: '0.5rem' }}>
                  Unable to load payment amount
                </Heading>
                <Text>
                  We&apos;ve logged the issue and we&apos;re working on a fix.
                  For help in the meantime,{' '}
                  <Link
                    as="button"
                    onClick={(): void => {
                      window.Brainfish?.Widgets?.open()
                    }}
                  >
                    contact support
                  </Link>
                  .
                </Text>
              </>,
            )
          }
          setInvoiceData(undefined)
        }
      }
    },
    [fetchPreviewInvoice, showErrorToast],
  )

  const clearData = useCallback(() => {
    requestIdRef.current++
    setInvoiceData(undefined)
  }, [])

  return useMemo(
    () => ({
      fetchAnnualUpgradePreview,
      loading,
      error: error || undefined,
      data: invoiceData,
      clearData,
    }),
    [fetchAnnualUpgradePreview, loading, error, invoiceData, clearData],
  )
}
